import { useState, useEffect, useMemo } from 'react';
import { format, addMonths, subMonths } from 'date-fns';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  TrendingUp,
  CreditCard,
  Filter,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Card, CardHeader, CardContent, Button, Select, LoadingSpinner } from '../components/ui';
import { getLedger, getDateRange, getUsers, getSettings } from '../services/api';
import type { Subscription, User, DateRangeBreakdown } from '../types';
import { useAuth } from '../contexts/AuthContext';

function generateColorFromCategory(categoryName: string): string {
  let hash = 0;
  const input = String(categoryName || 'Other');
  for (let index = 0; index < input.length; index += 1) {
    hash = input.charCodeAt(index) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 70%, 55%)`;
}

export default function Dashboard() {
  const { user } = useAuth();
  const isRootAdmin = Boolean(user?.isRootAdmin);
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [dateRangeData, setDateRangeData] = useState<DateRangeBreakdown[]>([]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [defaultCurrency, setDefaultCurrency] = useState('INR');
  const [categoryColors, setCategoryColors] = useState<Record<string, string>>({});
  const [conversionRates, setConversionRates] = useState<Record<string, number>>({});
  const [dateRange, setDateRange] = useState({
    start: format(subMonths(new Date(), 5), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd'),
  });

  // Fetch data
  useEffect(() => {
    fetchData();
  }, [currentMonth, selectedUser]);

  useEffect(() => {
    fetchUsers();
    fetchSettings();
  }, []);

  useEffect(() => {
    fetchDateRangeData();
  }, [dateRange, selectedUser]);

  useEffect(() => {
    fetchConversionRates();
  }, [subscriptions, defaultCurrency]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const monthStr = format(currentMonth, 'yyyy-MM');
      const response = await getLedger(monthStr, selectedUser || undefined);
      if (response.success) {
        setSubscriptions(response.subscriptions);
      }
    } catch (error) {
      console.error('Failed to fetch ledger:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await getUsers();
      if (response.success) {
        setUsers(response.users);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await getSettings();
      if (response.success && response.settings?.currency) {
        setDefaultCurrency(response.settings.currency);
        setCategoryColors(response.settings.categoryColors || {});
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchDateRangeData = async () => {
    try {
      const response = await getDateRange(dateRange.start, dateRange.end, selectedUser || undefined);
      if (response.success) {
        setDateRangeData(response.breakdown);
      }
    } catch (error) {
      console.error('Failed to fetch date range data:', error);
    }
  };

  const fetchConversionRates = async () => {
    const sourceCurrencies = Array.from(new Set(
      subscriptions
        .map((sub) => sub.currency || defaultCurrency)
        .filter((currency) => currency && currency !== defaultCurrency)
    ));

    if (!sourceCurrencies.length) {
      setConversionRates({});
      return;
    }

    const fetchRate = async (fromCurrency: string, toCurrency: string): Promise<number | null> => {
      try {
        const primaryResponse = await fetch(`https://api.frankfurter.app/latest?from=${fromCurrency}&to=${toCurrency}`);
        if (primaryResponse.ok) {
          const primaryData = await primaryResponse.json() as { rates?: Record<string, number> };
          const primaryRate = primaryData?.rates?.[toCurrency];
          if (typeof primaryRate === 'number') {
            return primaryRate;
          }
        }
      } catch {
      }

      try {
        const fallbackResponse = await fetch(`https://open.er-api.com/v6/latest/${fromCurrency}`);
        if (!fallbackResponse.ok) return null;
        const fallbackData = await fallbackResponse.json() as { rates?: Record<string, number> };
        const fallbackRate = fallbackData?.rates?.[toCurrency];
        return typeof fallbackRate === 'number' ? fallbackRate : null;
      } catch {
        return null;
      }
    };

    const results = await Promise.all(
      sourceCurrencies.map(async (currency) => {
        const rate = await fetchRate(currency, defaultCurrency);
        return (typeof rate === 'number') ? { currency, rate } : null;
      })
    );

    const rateMap = results.reduce<Record<string, number>>((acc, item) => {
      if (item) {
        acc[item.currency] = item.rate;
      }
      return acc;
    }, {});

    setConversionRates(rateMap);
  };

  // Computed values
  const activeSubscriptions = useMemo(
    () => subscriptions.filter((s) => s.status === 'active'),
    [subscriptions]
  );

  const convertToDefaultCurrency = (amount: number, fromCurrency: string) => {
    if (!fromCurrency || fromCurrency === defaultCurrency) {
      return amount;
    }
    const rate = conversionRates[fromCurrency];
    if (typeof rate !== 'number') {
      return amount;
    }
    return amount * rate;
  };

  const monthlyTotal = useMemo(() => {
    return subscriptions.reduce((acc, sub) => acc + convertToDefaultCurrency(sub.amount, sub.currency), 0);
  }, [subscriptions, conversionRates, defaultCurrency]);

  const categoryBreakdown = useMemo(() => {
    const breakdown: Record<string, { value: number; count: number }> = {};
    activeSubscriptions.forEach((sub) => {
      const category = sub.category || 'Other';
      const convertedAmount = convertToDefaultCurrency(sub.amount, sub.currency);
      if (!breakdown[category]) {
        breakdown[category] = { value: 0, count: 0 };
      }
      breakdown[category].value += convertedAmount;
      breakdown[category].count += 1;
    });
    return Object.entries(breakdown).map(([name, entry]) => ({ name, value: entry.value, count: entry.count }));
  }, [activeSubscriptions, conversionRates, defaultCurrency]);

  const categoryColorMap = useMemo(() => {
    return categoryBreakdown.reduce<Record<string, string>>((acc, item) => {
      const selectedColor = categoryColors[item.name];
      acc[item.name] = /^#[0-9a-fA-F]{6}$/.test(String(selectedColor || ''))
        ? selectedColor
        : generateColorFromCategory(item.name);
      return acc;
    }, {});
  }, [categoryBreakdown, categoryColors]);

  const getCategoryColor = (categoryName: string) => categoryColorMap[categoryName] || generateColorFromCategory(categoryName);

  const yearlyProjection = useMemo(() => {
    return activeSubscriptions.reduce((acc, sub) => {
      const convertedAmount = convertToDefaultCurrency(sub.amount, sub.currency);
      if (sub.billingCycle === 'monthly') {
        return acc + convertedAmount * 12;
      }
      return acc + convertedAmount;
    }, 0);
  }, [activeSubscriptions, conversionRates, defaultCurrency]);

  const chartData = useMemo(() => {
    return dateRangeData.map((d) => ({
      name: `${d.monthName.slice(0, 3)}`,
      total: d.total,
    }));
  }, [dateRangeData]);

  const handlePrevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: defaultCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);

  if (loading && !subscriptions.length) {
    return (
      <div className="h-full flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-dark-400 mt-1">Overview of your subscriptions</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            options={[
              { value: '', label: isRootAdmin ? 'All Families' : 'All Members' },
              ...users.map((u) => ({ value: u.id, label: u.name })),
            ]}
            className="w-40"
          />
          
          <div className="flex items-center bg-dark-800 border border-dark-700 rounded-lg">
            <button
              onClick={handlePrevMonth}
              className="p-2 text-dark-400 hover:text-white transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="px-3 text-white font-medium min-w-[140px] text-center">
              {format(currentMonth, 'MMMM yyyy')}
            </span>
            <button
              onClick={handleNextMonth}
              className="p-2 text-dark-400 hover:text-white transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary-500/20 rounded-xl flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-primary-400" />
            </div>
            <div>
              <p className="text-dark-400 text-sm">Monthly Cost</p>
              <p className="text-2xl font-bold text-white">{formatCurrency(monthlyTotal)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-dark-400 text-sm">Yearly Projection</p>
              <p className="text-2xl font-bold text-white">{formatCurrency(yearlyProjection)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <p className="text-dark-400 text-sm">Active Subscriptions</p>
              <p className="text-2xl font-bold text-white">{activeSubscriptions.length}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center">
              <Calendar className="w-6 h-6 text-orange-400" />
            </div>
            <div>
              <p className="text-dark-400 text-sm">Total Subscriptions</p>
              <p className="text-2xl font-bold text-white">{subscriptions.length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trend Chart */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Monthly Trend</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDatePicker(!showDatePicker)}
            >
              <Filter className="w-4 h-4" />
              Date Range
            </Button>
          </CardHeader>
          {showDatePicker && (
            <div className="px-6 py-3 border-b border-dark-700 flex items-center gap-3">
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="px-3 py-1.5 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
              />
              <span className="text-dark-400">to</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="px-3 py-1.5 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
              />
            </div>
          )}
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip
                    cursor={{ fill: 'rgba(59, 130, 246, 0.14)' }}
                    wrapperStyle={{ outline: 'none' }}
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      border: '1px solid #334155',
                      borderRadius: '8px',
                      boxShadow: '0 10px 30px rgba(2, 6, 23, 0.45)',
                    }}
                    labelStyle={{ color: '#fff' }}
                    formatter={(value: number) => [formatCurrency(value), 'Total']}
                  />
                  <Bar
                    dataKey="total"
                    fill="#3b82f6"
                    radius={[4, 4, 0, 0]}
                    activeBar={{ fill: '#60a5fa', stroke: '#93c5fd', strokeWidth: 1 }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Category Breakdown */}
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold text-white">By Category</h2>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center">
              {categoryBreakdown.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={categoryBreakdown}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {categoryBreakdown.map((item) => (
                        <Cell key={`cell-${item.name}`} fill={getCategoryColor(item.name)} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1e293b',
                        border: '1px solid #334155',
                        borderRadius: '8px',
                        color: '#e2e8f0',
                      }}
                      itemStyle={{ color: '#e2e8f0' }}
                      labelStyle={{ color: '#e2e8f0' }}
                      formatter={(value: number, _name: string, entry: { payload?: { name?: string; count?: number } }) => {
                        const categoryName = entry?.payload?.name || 'Other';
                        const count = entry?.payload?.count || 0;
                        return [
                          <span style={{ color: getCategoryColor(categoryName) }}>{`${formatCurrency(value)} (${count} active)`}</span>,
                          'Amount',
                        ];
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-dark-400 text-center w-full">No data available</p>
              )}
            </div>
            <div className="flex flex-wrap gap-3 mt-4 justify-center">
              {categoryBreakdown.map((item) => (
                <div key={item.name} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: getCategoryColor(item.name) }}
                  />
                  <span className="text-sm" style={{ color: getCategoryColor(item.name) }}>{item.name}</span>
                  <span className="text-xs" style={{ color: getCategoryColor(item.name) }}>{`${formatCurrency(item.value)} • ${item.count} active`}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Subscriptions */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold text-white">Active Subscriptions</h2>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-dark-700">
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-dark-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-dark-400 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-dark-400 uppercase tracking-wider">
                    Billing
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-dark-400 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-dark-400 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-700">
                {activeSubscriptions.length > 0 ? (
                  activeSubscriptions.map((sub) => (
                    <tr key={sub.id} className="hover:bg-dark-700/50 transition-colors">
                      <td className="px-4 sm:px-6 py-4">
                        <div className="text-white font-medium">{sub.name}</div>
                        <div className="text-dark-400 text-sm">Day {sub.billingDay}</div>
                      </td>
                      <td
                        className="px-4 sm:px-6 py-4"
                        style={{ color: getCategoryColor(sub.category || 'Other') }}
                      >
                        {sub.category || 'Other'}
                      </td>
                      <td className="px-4 sm:px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          sub.billingCycle === 'monthly' 
                            ? 'bg-blue-500/20 text-blue-400' 
                            : 'bg-purple-500/20 text-purple-400'
                        }`}>
                          {sub.billingCycle}
                        </span>
                      </td>
                      <td
                        className="px-4 sm:px-6 py-4 font-medium"
                        style={{ color: getCategoryColor(sub.category || 'Other') }}
                      >
                        {formatCurrency(convertToDefaultCurrency(sub.amount, sub.currency))}
                      </td>
                      <td className="px-4 sm:px-6 py-4">
                        <span className="px-2 py-1 text-xs rounded-full bg-green-500/20 text-green-400">
                          {sub.status}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-4 sm:px-6 py-8 text-center text-dark-400">
                      No active subscriptions found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
